import { scaleLinear } from 'd3-scale';
import { axisBottom, axisLeft, axisRight } from 'd3-axis';
import { format } from 'd3-format';
import {
  BASE_PRESSURE,
  PRESSURE_LINES,
  PRESSURE_TICKS,
  TAN_BOTTOM,
  TAN_TOP,
  BREAKING_PRESSURE,
  BREAKING_FRACTION,
} from './constants.js';

/**
 * @typedef {Object} SoundingDatum
 * @property {number} press
 * @property {number} temp
 * @property {number} [dwpt]
 */

/**
 * @typedef {Object} Scales
 * @property {number} w  - Inner plot width in pixels
 * @property {number} h  - Inner plot height in pixels
 * @property {import('d3-scale').ScaleLinear<number,number>} x - Temperature scale
 * @property {Function} y      - Pressure -> pixel (piecewise log)
 * @property {Function} invY   - Pixel -> pressure
 * @property {Function} skewX  - (t, p) -> pixel X (with skew offset)
 * @property {Function} invSkewX - (xPx, yPx) -> temperature
 * @property {Function} xAxis
 * @property {Function} yAxis
 * @property {Function} yAxis2
 * @property {number} tMax - Upper bound of temperature domain (°C)
 * @property {number} tMin - Lower bound of temperature domain (°C)
 * @property {number} basep
 * @property {number} topp
 * @property {number} hTop    - Pixel allocation for upper portion
 * @property {number} hBottom - Pixel allocation for lower portion
 */

/**
 * Build scales for a Skew-T chart using Skysight-style piecewise skew and a
 * piecewise log Y scale stitched at BREAKING_PRESSURE.
 *
 * The function supports two call signatures for backward compatibility with
 * the upstream lib's SkewT.js renderer:
 *
 *   - Modern (preferred): `createScales(width, height, data)`
 *   - Legacy:             `createScales(width, margin)` — height is derived
 *     from width (square plot) and data defaults to `[]`.
 *
 * @param {number} width
 * @param {number|{top:number,right:number,bottom:number,left:number}} heightOrMargin
 * @param {SoundingDatum[]} [data]
 * @returns {Scales}
 */
export function createScales(width, heightOrMargin, data) {
  // -- Resolve overloaded signature ------------------------------------------
  let w;
  let h;
  if (typeof heightOrMargin === 'number') {
    // Modern: createScales(width, height, data)
    w = width;
    h = heightOrMargin;
  } else {
    // Legacy: createScales(width, margin) — derive inner w/h from margins,
    // matching the upstream lib's square-plot convention.
    const margin = heightOrMargin || { top: 0, right: 0, bottom: 0, left: 0 };
    w = width - margin.left - margin.right;
    h = width - margin.top - margin.bottom;
  }
  const sounding = Array.isArray(data) ? data : [];

  // -- Pressure range --------------------------------------------------------
  // Bottom of chart = highest pressure in data (surface), defaulting to
  // BASE_PRESSURE if data is empty. Top of chart = 150 hPa per the Skysight
  // bundle (explicit override of the upstream lib's TOP_PRESSURE=100 hPa).
  // Data points above 150 hPa are clipped by the chart's clipPath.
  const basep = sounding.length
    ? Math.max(...sounding.map((d) => d.press))
    : BASE_PRESSURE;
  const topp = 150;

  // -- Piecewise Y scale -----------------------------------------------------
  // BREAKING_FRACTION (0.25) of pixels are allocated to the UPPER portion
  // (low pressure: [BREAKING_PRESSURE → topp], stratosphere). The remaining
  // 0.75 go to the LOWER portion (high pressure: [basep → BREAKING_PRESSURE],
  // troposphere where pilots fly). This matches Skysight: the surface layer
  // gets the bulk of the chart's vertical space, the stratosphere is
  // compressed. Variable naming matches Skysight bundle: `hTop = h * bf`.
  //
  // Pixel y=0 is at the top (topp), pixel y=h is at the bottom (basep),
  // and y(BREAKING_PRESSURE) = hTop = h * BREAKING_FRACTION.
  //
  // If BREAKING_PRESSURE falls outside (topp, basep), collapse to a single
  // log scale.
  const hasSplit = topp < BREAKING_PRESSURE && BREAKING_PRESSURE < basep;
  const hTop = hasSplit ? h * BREAKING_FRACTION : 0;     // top portion px
  const hBottom = h - hTop;                              // bottom portion px
  const yBreak = hTop; // pixel y where BREAKING_PRESSURE sits

  const logBasep = Math.log(basep);
  const logTopp = Math.log(topp);
  const logBreak = Math.log(BREAKING_PRESSURE);

  const y = (p) => {
    if (!hasSplit) {
      return (h * (Math.log(p) - logTopp)) / (logBasep - logTopp);
    }
    if (p >= BREAKING_PRESSURE) {
      // Bottom portion: yBreak (at p=BREAKING_PRESSURE) → h (at p=basep)
      const f = (Math.log(p) - logBreak) / (logBasep - logBreak);
      return yBreak + f * hBottom;
    }
    // Top portion: 0 (at p=topp) → yBreak (at p=BREAKING_PRESSURE)
    const f = (Math.log(p) - logTopp) / (logBreak - logTopp);
    return f * yBreak;
  };

  const invY = (yPx) => {
    if (!hasSplit) {
      const f = yPx / h;
      return Math.exp(logTopp + f * (logBasep - logTopp));
    }
    if (yPx >= yBreak) {
      const f = (yPx - yBreak) / hBottom;
      return Math.exp(logBreak + f * (logBasep - logBreak));
    }
    const f = yPx / yBreak;
    return Math.exp(logTopp + f * (logBreak - logTopp));
  };

  // -- Skew offset (piecewise) ----------------------------------------------
  // Mirrors Skysight bundle: divide by tangent (not multiply).
  //
  //   skewX(t, p) = x(t) + offset(p)
  //   offset(p) =
  //     - if no split:        (y(basep) - y(p)) / TAN_BOTTOM
  //     - if p >= BREAKING_P: (y(basep) - y(p)) / TAN_BOTTOM
  //     - else:               (y(basep) - y(BREAKING_P)) / TAN_BOTTOM
  //                         + (y(BREAKING_P) - y(p)) / TAN_TOP
  const yBase = y(basep);
  const yBreakPress = y(BREAKING_PRESSURE);
  const skewOffsetAt = (p) => {
    if (!hasSplit) {
      return (yBase - y(p)) / TAN_BOTTOM;
    }
    if (p >= BREAKING_PRESSURE) {
      return (yBase - y(p)) / TAN_BOTTOM;
    }
    return (yBase - yBreakPress) / TAN_BOTTOM + (yBreakPress - y(p)) / TAN_TOP;
  };

  // -- Temperature domain (data-driven, Skysight-exact) ---------------------
  // Ported verbatim from docs/skysight-skewt-bundle.js:
  //
  //   tMax = max(data.temp) || 35
  //   if (tMax < 20) tMax = 20
  //   tMax = tMax + 20
  //
  //   const a = isSplit ? (y(basep) - y(breakingP))/tanBottom : 0
  //   tMin = data.filter(d => d.press > topp).reduce((u, e) => {
  //     const c = (isSplit && e.press <= breakingP)
  //       ? a + (y(breakingP) - y(e.press))/tanTop
  //       : (y(basep) - y(e.press))/tanBottom
  //     const m = (w * e.dwpt + tMax * c) / (c + w)  // tMin that puts dewpoint at left edge
  //     return m < u ? m - 1 : u                      // keep running min, pad 1°C
  //   }, 50)
  const dataTempMax = sounding.length
    ? Math.max(...sounding.map((d) => d.temp))
    : 35;
  let tMax = dataTempMax;
  if (tMax < 20) tMax = 20;
  tMax = tMax + 20;

  const a = hasSplit ? (yBase - yBreakPress) / TAN_BOTTOM : 0;
  const tMin = sounding
    .filter((d) => d.press > topp && Number.isFinite(d.dwpt))
    .reduce((u, e) => {
      const c =
        hasSplit && e.press <= BREAKING_PRESSURE
          ? a + (yBreakPress - y(e.press)) / TAN_TOP
          : (yBase - y(e.press)) / TAN_BOTTOM;
      const m = (w * e.dwpt + tMax * c) / (c + w);
      return m < u ? m - 1 : u;
    }, 50);

  const x = scaleLinear().domain([tMin, tMax]).range([0, w]);

  const skewX = (t, p) => x(t) + skewOffsetAt(p);
  const invSkewX = (xPx, yPx) => {
    const p = invY(yPx);
    const offset = skewOffsetAt(p);
    return x.invert(xPx - offset);
  };

  // Attach `invert`, `range`, `domain`, and `copy` to the piecewise `y`
  // function so existing callers (tooltips.js calls `y.invert`; d3-axis
  // requires `range/copy/domain`) keep working with the non-d3 piecewise
  // implementation.
  y.invert = invY;
  y.range = () => [0, h];
  y.domain = () => [topp, basep];
  y.copy = () => y; // safe: scale state is immutable for our use

  // -- Axes (preserved from upstream for background/tooltip compatibility) --
  const xAxis = axisBottom(x).tickSize(0).ticks(10);
  const yAxis = axisLeft(y)
    .tickSize(0)
    .tickValues(PRESSURE_LINES)
    .tickFormat(format('.0f'));
  const yAxis2 = axisRight(y)
    .tickSize(5)
    .tickValues(PRESSURE_TICKS)
    .tickFormat(() => '');

  return {
    w,
    h,
    x,
    y,
    invY,
    skewX,
    invSkewX,
    xAxis,
    yAxis,
    yAxis2,
    tMax,
    tMin,
    basep,
    topp,
    hTop,
    hBottom,
  };
}
